<script setup lang="ts">
import UiIcon, { type UiIconProps } from './UiIcon.vue';

export type InfoBoxVariant = 'info' | 'warning' | 'success' | 'error';

interface Props {
  variant?: InfoBoxVariant;
  title?: string;
  icon?: UiIconProps['name'];
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'info',
  title: undefined,
  icon: undefined,
});

const variantStyles: Record<InfoBoxVariant, { bg: string; border: string; iconColor: string; titleColor: string }> = {
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconColor: 'text-blue-500',
    titleColor: 'text-blue-800',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconColor: 'text-amber-500',
    titleColor: 'text-amber-800',
  },
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    iconColor: 'text-green-500',
    titleColor: 'text-green-800',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    iconColor: 'text-red-500',
    titleColor: 'text-red-800',
  },
};

const defaultIcons: Record<InfoBoxVariant, string> = {
  info: 'info-circle' as const,
  warning: 'info-circle' as const,
  success: 'check-circle' as const,
  error: 'info-circle' as const,
};

const styles = variantStyles[props.variant];
const iconName = props.icon || (defaultIcons[props.variant] as UiIconProps['name']);
</script>

<template>
  <div 
    :class="[
      styles.bg, 
      styles.border, 
      'border rounded-lg p-4'
    ]"
    role="note"
  >
    <div class="flex gap-3">
      <UiIcon 
        :name="iconName" 
        :class="[styles.iconColor, 'w-5 h-5 shrink-0 mt-0.5']" 
      />
      <div class="flex-1 min-w-0">
        <p 
          v-if="title" 
          :class="[styles.titleColor, 'font-medium text-sm mb-1']"
        >
          {{ title }}
        </p>
        <div class="text-sm text-gray-700">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

